// Copyright (c) 2020-2024 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react';
import { withStyles } from "tss-react/mui";
import { Grid, Tooltip, CircularProgress } from '@mui/material';
import cx from 'classnames';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import orderBy from 'lodash/orderBy';

import Ellipsis from '@shared/components/Ellipsis';
import Flex from '@shared/components/Flex';
import Button from '@shared/components/Button';
import RemoveIcon from '@shared/icons/Remove';
import EditIcon from '@shared/icons/Edit';
import CommentIcon from '@shared/icons/Comment';
import { showConfirmation } from '@shared/components/Confirmation';
import { showNotification, NotificationType } from '@shared/components/Notification';
import OrdersStore from '@shared/stores/orders';
import DeliverablesStore from '@shared/stores/deliverables';
import { DELIVERABLE_STATUS_LABELS, DeliverableStatus, DELIVERABLE_FIELDS } from '@shared/constants/deliverable';
import Deliverable, { DeliverableDTO } from '@shared/models/deliverable';
import { AssetDTO } from '@shared/models/asset';
import container from '@core/di';
import { getQueries, setQueries, getTestAttr, getDefaultError, formatDate } from '@shared/utils/common';
import { ManageMode } from '@shared/components/Form';
import Comments from '@shared/components/Comments';
import DeliverableManagement, { DeliverableManagementProps } from '@modules/Private/submodules/Orders/components/Deliverable/DeliverableManagement';
import DownloadInstructions from '@modules/Private/submodules/Orders/components/Deliverable/DownloadInstructions';

import { Id } from '@shared/types/common';

type ManagementProps = Pick<DeliverableManagementProps, 'onSubmit' | 'formComponentProps' | 'maxDueDate' | 'defaultDueDate' | 'orderManageMode'>;

// Prop interface for the main class.
export interface DeliverablesTableProps extends RouteComponentProps {
  className?: string;
  classes?: Record<string, string>;
  data: Array<Deliverable>;
  assetId: AssetDTO['id'];
  managementProps: ManagementProps;
  columns?: Array<string>;
  placeholder?: string;
  withManagement?: boolean;
  withConfirmationOnDelete?: boolean;
  setInitialRoute?: () => any;
  canEdit?: (assetId: AssetDTO['id'], deliverableType: DeliverableDTO['deliverableType']) => boolean;
  doesDeliverableExistInOrder?: (id: DeliverableDTO['id']) => boolean;
  onDelete: (assetId: AssetDTO['id'], deliverable: { id: DeliverableDTO['id']; status: DeliverableDTO['status'] }) => any;
}

export const COLUMNS = {
  name: 'name',
  datumId: 'datumId',
  projectionId: 'projectionId',
  deadline: 'deadline',
  status: 'status',
  download: 'download',
  actions: 'actions',
};

const moduleName = 'Order-management';

// Class to show the deliverables table section on the Order/Assets, deliverables & job requests page.
@observer
class DeliverablesTable extends React.Component<DeliverablesTableProps> {
  static defaultProps = {
    data: [],
    columns: [
      COLUMNS.name,
      COLUMNS.projectionId,
      COLUMNS.datumId,
      COLUMNS.deadline,
      COLUMNS.actions
    ],
    withManagement: true,
    placeholder: 'Please add at least one deliverable to the added asset',
  };
  private ordersStore = container.get<OrdersStore>(OrdersStore.diToken);
  private deliverablesStore = container.get<DeliverablesStore>(DeliverablesStore.diToken);
  @observable loading: { [deliverableId: string]: boolean } = {};

  constructor(props: DeliverablesTableProps) {
    super(props);
    makeObservable(this);
  }

  private get manageMode() {
    return getQueries().deliverableManageMode as ManageMode;
  }

  get availableDeliverableTypes() {
    return this.ordersStore.getAvailableDeliverableTypes(this.props.assetId);
  }

  private get defaultDeliverable() {
    const { managementProps } = this.props;
    const sortedData = orderBy(
      this.ordersStore.allDeliverables,
      ({ createdAt, id }) => managementProps.orderManageMode === ManageMode.create ? -createdAt : -id
    );

    return sortedData[0];
  }

  // Build and return the column for a deliverable row.
  private get columns() {
    const { columns, withManagement, doesDeliverableExistInOrder = () => false } = this.props;
    const classes = withStyles.getClasses(this.props) as Record<string, any>;

    return [
      {
        name: 'Deliverable',
        className: classes.nameCell,
        columnId: COLUMNS.name,
        withTooltip: false,
        field: DELIVERABLE_FIELDS.deliverableType,
        render: ({ deliverableType, deliverableTypeName, isInternal }: Deliverable) => {
          const deliverable = this.deliverablesStore.deliverableTypes.find((item) => deliverableType == item.id);

          const name = (deliverableTypeName || deliverable?.name) as string;

          return (
            <Flex wrap="nowrap">
              <Ellipsis text={name} />
              {isInternal && <span className={classes.internalLabel}>Internal</span>}
            </Flex>
          );
        }
      },
      //{
      //  name: 'Datum',
      //  className: classes.projectionCell,
      //  columnId: COLUMNS.datumId,
      //  field: DELIVERABLE_FIELDS.datumId,
      //  render: ({ datumId, datumName }: Deliverable) => {
      //    const deliverableDatum = this.deliverablesStore.deliverableDatums.find((datum) => datumId == datum.id);

      //    return datumName || deliverableDatum?.name;
      //  }
      //},
      //{
      //  name: 'Projection',
      //  className: classes.projectionCell,
      //  columnId: COLUMNS.projectionId,
      //  field: DELIVERABLE_FIELDS.projectionId,
      //  render: ({ projectionId, projectionName }: Deliverable) => {
      //    const deliverableProjection = this.deliverablesStore.deliverableProjections.find((projection) => projectionId == projection.id);

      //    return projectionName || deliverableProjection?.name;
      //  }
      //},
      {
        name: 'Due',
        className: classes.deadlineCell,
        columnId: COLUMNS.deadline,
        withTooltip: false,
        render: ({ deadline }: Deliverable) => formatDate(deadline)
      },
      {
        name: 'Status',
        className: classes.statusCell,
        columnId: COLUMNS.status,
        field: DELIVERABLE_FIELDS.deadline,
        render: ({ status }: Deliverable) => DELIVERABLE_STATUS_LABELS[status]
      },
      {
        name: 'Download',
        className: classes.downloadCell,
        columnId: COLUMNS.download,
        field: DELIVERABLE_FIELDS.download,
        withTooltip: false,
        render: ({ id, status }: Deliverable) => {
          if (status === DeliverableStatus.completed) {
            return (
              <span
                {...getTestAttr('Order-details-download', 'download-instruction', 'btn')}
                className={classes.instructionBtnLink}
                data-id={id}
                onClick={this.handleShowDownloadInstruction}
              >
                Instructions
              </span>
            );
          }

          return <span>N/A</span>;
        }
      },
      {
        name: '',
        className: classes.actionsCell,
        columnId: COLUMNS.actions,
        withTooltip: false,
        render: (deliverable: Deliverable) => {
          if (this.loading[deliverable.id]) {
            return (
              <Flex justifyContent="flex-end" classes={{ root: classes.loadingWrapper }}>
                <CircularProgress classes={{ root: classes.loading }} size={15} />
              </Flex>
            );
          }

          const canComment = doesDeliverableExistInOrder(deliverable.id);

          return (
            <Flex
              wrap="nowrap"
              justifyContent="flex-end"
            >
              {canComment && (
                <CommentIcon
                  classes={{ root: classes.actionBtn }}
                  data-id={deliverable.id}
                  onClick={this.handleShowComments}
                />
              )}
              <EditIcon
                {...getTestAttr('Deliverables-management', 'edit', 'btn')}
                className={classes.actionBtn}
                data-id={deliverable.id}
                onClick={this.handleEdit}
              />
              <RemoveIcon
                {...getTestAttr('Deliverables-management', 'delete', 'btn')}
                className={cx(classes.actionBtn, { [classes.actionBtnDisabled]: !withManagement })}
                data-id={deliverable.id}
                data-status={deliverable.status}
                onClick={this.handleDelete}
              />
            </Flex>
          );
        }
      },
    ].filter(({ columnId }) => columns?.includes(columnId));
  }

  private get content() {
    const { data, placeholder } = this.props;
    const classes = withStyles.getClasses(this.props) as Record<string, any>;

    if (data.length === 0) {
      return (
        <Grid container justifyContent="center" className={classes.noData}>
          {placeholder}
        </Grid>
      );
    }

    // Show the deliverable rows 
    return (
      <div className={classes.table}>
        <Flex
          wrap="nowrap"
          alignItems="center"
          justifyContent="space-between"
          className={cx(classes.cells, classes.head)}
        >
          {this.columns.map(({ name, columnId, className }) => ( // headers for deli rows.
            <Ellipsis
              key={columnId}
              classes={{ root: cx(classes.cell, classes.cellHeading, className) }}
              text={name}
            />
          ))}
        </Flex>

        {data.map((deliverable) => ( // deli rows.
          <Flex
            wrap="nowrap"
            alignItems="center"
            justifyContent="space-between"
            key={deliverable.id}
            className={cx(classes.cells, classes.row)}
          >
            {this.columns.map(({ columnId, field = '', withTooltip = true, render, className }) => ( // deli cols.
              <Ellipsis
                withTooltip={withTooltip}
                key={columnId}
                classes={{ root: cx(classes.cell, classes.cellBody, className) }}
                text={render ? render(deliverable) : deliverable[field]}
              />
            ))}
          </Flex>
        ))}
      </div>
    );
  }

  private get openState(): { management: boolean; comments: boolean; downloadInstructions: boolean } {
    const { assetId } = this.props;
    const queries = getQueries();

    return {
      management: queries.assetId == assetId && Boolean(this.manageMode),
      comments: queries.assetId == assetId && Boolean(queries.comments) && Boolean(queries.deliverableId),
      downloadInstructions: queries.assetId == assetId && Boolean(queries.downloadInstructions),
    };
  }

  private setLoading = (id: DeliverableDTO['id'], state: boolean) => {
    this.loading = {
      ...this.loading,
      [id]: state
    };
  }

  private handleAdd = () => {
    const { assetId } = this.props;

    setQueries({ assetId, deliverableManageMode: ManageMode.create });
  }

  private handleEdit = (e) => {
    const { assetId } = this.props;
    const { id } = e.currentTarget.dataset;

    setQueries({ assetId, deliverableId: id, deliverableManageMode: ManageMode.edit });
  }

  private handleDelete = (e) => {
    const { assetId, onDelete, withConfirmationOnDelete } = this.props;
    const { id, status } = e.currentTarget.dataset;

    const deleteDeliverable = async (confirmed = true) => {
      if (!confirmed) {
        return;
      }

      this.setLoading(id, true);

      try {
        await onDelete(assetId, { id, status });
        this.setLoading(id, false);
      } catch (err) {
        this.setLoading(id, false);
        showNotification(getDefaultError(err.response.data.errors), NotificationType.error);
      }
    };

    if (withConfirmationOnDelete) {
      showConfirmation(
        { title: 'Delete deliverable', question: 'Are you sure you want to delete this deliverable? This action cannot be undone.' },
        deleteDeliverable
      );

      return;
    }

    deleteDeliverable();
  }

  private handleShowDownloadInstruction = (e) => {
    const { id } = e.currentTarget.dataset;
    const { assetId } = this.props;

    setQueries({ assetId, deliverableId: id, downloadInstructions: true });
  }

  private handleShowComments = (e) => {
    const { id } = e.currentTarget.dataset;
    const { assetId } = this.props;

    setQueries({ assetId, deliverableId: id, comments: true });
  }

  private handleCommentDialogClose = async (id: Id) => {
    const { setInitialRoute = () => {} } = this.props;

    setInitialRoute();
  }

  render() {
    const {
      assetId,
      data,
      managementProps,
      withManagement,
      setInitialRoute = () => {},
      doesDeliverableExistInOrder = () => false
    } = this.props;
    const classes = withStyles.getClasses(this.props) as Record<string, any>;

    const queries = getQueries();
    const noAvailableDeliverableTypes = Boolean(this.deliverablesStore.deliverableTypes.length && !this.availableDeliverableTypes.length);

    // Note: data won't be updated after changes made in the Comments dialog.  
    // So the dialog may not show the correct exceptionCode in subsequent opens.
    // WXYZ to fix, set a flag to tell partent component to refresh the data.
    const deliId = queries.deliverableId as Id;
    const curDeli = data.find((item) => item.id == deliId);

    return (
      <div className={classes.root}>
        {this.openState.management && (
          <DeliverableManagement
            manageMode={this.manageMode}
            assetId={assetId}
            deliverableId={queries.deliverableId as number}
            doesDeliverableExistInOrder={doesDeliverableExistInOrder(queries.deliverableId as number)}
            defaultDatum={this.defaultDeliverable?.datumId !== undefined
              ? { id: this.defaultDeliverable.datumId, name: this.defaultDeliverable.datumName }
              : undefined
            }
            defaultProjection={this.defaultDeliverable?.projectionId !== undefined
              ? { id: this.defaultDeliverable.projectionId, name: this.defaultDeliverable.projectionName }
              : undefined
            }
            {...managementProps}
          />
        )}
        { /* Show the DownloadInstructions dialog if downloadInstructions=true in the qStr. */
          this.openState.downloadInstructions && (
          <DownloadInstructions
            deliverableId={queries.deliverableId as number}
            close={setInitialRoute}
          />
        )}
        { /* Show the Comments dialog if comments=true in the qStr. */
          this.openState.comments && (
          <Comments
            id={queries.deliverableId as Id}
            data={this.deliverablesStore.commentsStore.list}
            isDeliverable={true}
            exceptionCode={curDeli?.exceptionCode}
            sendComment={this.deliverablesStore.commentsStore.send}
            getComments={this.deliverablesStore.commentsStore.getList}
            resetComments={this.deliverablesStore.commentsStore.reset}
            close={this.handleCommentDialogClose.bind(this)}
          />
        )}

        <Flex alignItems="center">
          <h6 className={classes.title}>Deliverables</h6>
          {withManagement && (
            <Tooltip
              title={noAvailableDeliverableTypes ? 'All deliverables are added to the asset' : ''}
            >
              <span>
                <Button
                  {...getTestAttr(moduleName, 'new-deliverable', 'btn')}
                  text="Add new"
                  color="primary"
                  variant="outlined"
                  size="small"
                  disabled={noAvailableDeliverableTypes}
                  onClick={this.handleAdd}
                  className={classes.addButton}
                />
              </span>
            </Tooltip>
          )}

          {this.content}
        </Flex>
      </div >
    );
  }
}

const DeliverablesTableStyled = withStyles(
  DeliverablesTable, 
  (theme, props) => ({
    "root": {
      width: '100%',
      borderRadius: '4px',
      backgroundColor: theme.palette.colors.white,
      padding: theme.spacing(5),
    },
    "title": {
      fontSize: 17,
      color: theme.palette.colors.black,
      marginRight: theme.spacing(4),
    },
    "addButton": {
      margin: theme.spacing(0, 4),
    },
    "noData": {
      marginTop: theme.spacing(4),
      fontSize: 13,
      color: theme.palette.colors.greyLight3
    },
    "table": {
      width: '100%',
      marginTop: theme.spacing(4),
    },
    "head": {
      marginBottom: theme.spacing(),
    },
    "cells": {},
    "row": {
      marginBottom: theme.spacing(3),
      alignItems: 'flex-start'
    },
    "cell": {
      marginRight: theme.spacing(3),
    },
    "cellHeading": {
      fontSize: 12,
      color: theme.palette.colors.greyDark,
    },
    "cellBody": {},
    "nameCell": {
      width: '20%',
    },
    "projectionCell": {
      width: 180,
      whiteSpace: 'normal',
      overflow: 'auto'
    },
    "deadlineCell": {
      minWidth: 120,
    },
    "statusCell": {
      width: 200,
    },
    "actionsCell": {
      width: 100,
      minWidth: 100,
      paddingLeft: theme.spacing(),
      marginRight: 0,
    },
    "loadingWrapper": {
      padding: theme.spacing(1, 0)
    },
    "loading": {
      marginRight: theme.spacing()
    },
    "downloadCell": {
      width: 90
    },
    "internalLabel": {
      backgroundColor: theme.palette.colors.greyLight7,
      fontSize: 11,
      fontWeight: 500,
      borderRadius: 90,
      padding: theme.spacing(1, 2),
      marginLeft: theme.spacing(2)
    },
    "actionBtn": {
      cursor: 'pointer',
      marginLeft: theme.spacing(2),
    },
    "actionBtnDisabled": {
      ...theme.shortcuts.disabled
    },
    "instructionBtnLink": {
      ...theme.shortcuts.borderPrimarySolid,
    }
  })
);

export default withRouter(DeliverablesTableStyled);